import { creatorbase, creatorbaseQueries, PaginatedApiResponseData } from '@round/api';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateReportMutation } from '@round/creatorbase-helpers/api-hooks';
import useReportId from '../../hooks/useReportId';
import useInvalidateReportCampaigns from './useInvalidateReportCampaigns';
import { getReportCampaignsListParams } from './reportCampaignsListParams';

export function useReorderReportCampaigns(platform: creatorbase.Platform) {
    const queryClient = useQueryClient();
    const reportId = useReportId();
    const { update: updateReport } = useUpdateReportMutation(reportId);
    const invalidateReportCampaigns = useInvalidateReportCampaigns();

    return async (newOrder: number[]) => {
        if (!reportId) {
            return;
        }

        const queryKey = creatorbaseQueries.campaigns.keys.list(getReportCampaignsListParams(reportId, platform));
        const snapshot = queryClient.getQueryData<PaginatedApiResponseData<creatorbase.Campaign>>(queryKey);

        if (snapshot) {
            const reordered = newOrder
                .map((id) => snapshot.results.find((campaign) => campaign.id === id))
                .filter((campaign): campaign is creatorbase.Campaign => Boolean(campaign));
            const results = reordered.length === snapshot.results.length ? reordered : snapshot.results;
            queryClient.setQueryData<PaginatedApiResponseData<creatorbase.Campaign>>(queryKey, {
                ...snapshot,
                results,
            });
        }

        try {
            const updatedReport = await updateReport({ id: reportId, [`${platform}_campaign_ids_order`]: newOrder });
            invalidateReportCampaigns(platform);
            return { status: 200 as const, data: updatedReport };
        } catch (e) {
            if (!(e instanceof Error && e.name === 'AbortError') && snapshot) {
                queryClient.setQueryData(queryKey, snapshot);
            }
            throw e;
        }
    };
}
